import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import ClientForm from '@/components/clients/new_client/ClientForm';
import type { ClientFullConfigData, ClientDisplayData } from '@/pages/ClientsPage';
import type { ClientFormData } from '@/components/clients/new_client/utilities/formDataUtils';
import type { Role, RateMultiplier } from '@/types';

interface ClientFormPopupProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  clientData: Partial<ClientFullConfigData> | null;
  onSave: (formData: Partial<ClientFormData>) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  error: string | null;
  allClients?: ClientDisplayData[];
}

export const ClientFormPopup: React.FC<ClientFormPopupProps> = ({
  isOpen,
  onOpenChange,
  clientData,
  onSave,
  onCancel,
  isLoading,
  error,
  allClients,
}) => {
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const [clientName, setClientName] = useState(clientData?.name ?? '');

  const formReadyClientData = useMemo((): ClientFormData | null => {
    if (!clientData) {
      return null;
    }
    return {
      ...clientData,
      // The data from the DB is JSON, we need to cast it for the form.
      roles: (Array.isArray(clientData.roles) ? clientData.roles : []) as unknown as Role[],
      rate_multipliers: (Array.isArray(clientData.rate_multipliers) ? clientData.rate_multipliers : []) as unknown as RateMultiplier[],
      catalog_holiday_ids: Array.isArray((clientData as Record<string, unknown>).catalog_holiday_ids) ? (clientData as Record<string, unknown>).catalog_holiday_ids as string[] : [],
      custom_holiday_ids: Array.isArray((clientData as Record<string, unknown>).custom_holiday_ids) ? (clientData as Record<string, unknown>).custom_holiday_ids as string[] : [],
    } as ClientFormData;
  }, [clientData]);

  useEffect(() => {
    setClientName(clientData?.name ?? '');
  }, [clientData]);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('client-form-popup-open');
    } else {
      document.documentElement.classList.remove('client-form-popup-open');
    }
    return () => {
      document.documentElement.classList.remove('client-form-popup-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const rootElement = contentElement;
    if (!rootElement) return;

    const handleScroll = () => {
      setIsScrolled(rootElement.scrollTop > 10);
    };

    rootElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => rootElement.removeEventListener('scroll', handleScroll);
  }, [contentElement]);

  const isEditing = clientData ? !!clientData.id : false;
  const title = isEditing 
    ? 'Edit Client' 
    : (clientName && clientName.trim() ? clientName : 'Add New Client');
  const description = isEditing
    ? 'Update client profile and configurations.'
    : 'Create a new client profile and configurations.';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent 
        ref={setContentElement} 
        centerMode="viewport"
        className="sm:max-w-4xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Allow the X button to work by not preventing default for it
          const target = e.target as Element;
          if (target?.closest('[data-dialog-close]')) {
            return;
          }
          e.preventDefault();
        }}
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
        </VisuallyHidden>
        <div className="pt-4">
          {isLoading && <p>Saving client...</p>}
          {formReadyClientData && (
            <ClientForm
              clientData={formReadyClientData}
              onSave={onSave}
              onCancel={onCancel}
              isLoading={isLoading}
              error={error}
              isEditing={isEditing}
              onNameChange={setClientName}
              allClients={allClients}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
